import type { BillingOrganizationSummary } from '../../../types/adminBilling';
import {
  DISPLAY_STATUS_LABEL,
  REMINDER_OUTCOME_LABEL,
  billingModeDescription,
  billingModeLabel,
  dateOnlyCell,
  formatMoney,
  instantCell,
  planCellText,
} from './billingFormat';
import { AdminPanel } from '../AdminPage';

interface BillingSummaryPanelProps {
  summary: BillingOrganizationSummary;
}

/**
 * The state of this organization's billing in one block: what it is on, what it owes, who can be
 * told about it and when anybody last was.
 *
 * The contact counts are here rather than only in the contacts panel below because "nobody can be
 * reminded" is the fact that explains an untouched overdue balance, and an admin should meet it
 * beside the balance rather than after scrolling past a plan editor.
 *
 * **Which system bills the organization is stated first**, because everything else on the page
 * follows from it: whether an invoice can be raised here, whether mark paid and void work, and who
 * chases the money.
 */
export const BillingSummaryPanel = ({ summary }: BillingSummaryPanelProps) => {
  const hasOverdue = summary.overdueInvoiceCount > 0;
  /*
    Not a problem for a Stripe-managed organization: Stripe holds its contacts and does its own
    chasing, so having none here is expected rather than a gap. The API takes the same view of the
    list's own warning flag.
  */
  const noDeliverableContact = summary.activeToContactCount === 0 && !summary.isStripeManaged;

  return (
    <AdminPanel title="Summary" titleId="billing-summary-title">

      <p
        className={`billing-panel__banner billing-panel__banner--mode${
          summary.isStripeManaged ? ' billing-panel__banner--mode-stripe' : ''
        }`}
        data-testid="billing-summary-mode"
      >
        <span className="billing-panel__reminder-outcome">
          {billingModeLabel(summary.isStripeManaged)}
        </span>{' '}
        {billingModeDescription(summary.isStripeManaged)}
      </p>

      {noDeliverableContact && (
        <p
          className="billing-panel__banner billing-panel__banner--error"
          data-testid="billing-summary-no-contact"
        >
          There is no active To contact, so no reminder can be sent for this organization. Add one
          below.
        </p>
      )}

      <dl className="billing-panel__facts">
        <div className="billing-panel__fact">
          <dt className="billing-panel__fact-label">Plan</dt>
          <dd className="billing-panel__fact-value" data-testid="billing-summary-plan">
            {planCellText(
              summary.plan?.amountCents ?? null,
              summary.plan?.currency ?? null,
              summary.plan?.interval ?? null
            )}
          </dd>
        </div>
        <div className="billing-panel__fact">
          <dt className="billing-panel__fact-label">Next invoice</dt>
          <dd className="billing-panel__fact-value" data-testid="billing-summary-next-invoice">
            {dateOnlyCell(summary.nextInvoiceOn)}
          </dd>
        </div>
        <div className="billing-panel__fact">
          <dt className="billing-panel__fact-label">Outstanding</dt>
          <dd className="billing-panel__fact-value" data-testid="billing-summary-outstanding">
            {formatMoney(summary.outstandingCents, summary.plan?.currency ?? null)}
            <span className="billing-panel__fact-label">
              {' '}
              across {summary.outstandingInvoiceCount}{' '}
              {summary.outstandingInvoiceCount === 1 ? 'invoice' : 'invoices'}
            </span>
          </dd>
        </div>
        <div className="billing-panel__fact">
          <dt className="billing-panel__fact-label">Overdue</dt>
          <dd
            className={`billing-panel__fact-value${
              hasOverdue ? ' billing-panel__fact-value--danger' : ''
            }`}
            data-testid="billing-summary-overdue"
          >
            {hasOverdue
              ? `${formatMoney(summary.overdueCents, summary.plan?.currency ?? null)} across ${
                  summary.overdueInvoiceCount
                } ${summary.overdueInvoiceCount === 1 ? 'invoice' : 'invoices'}`
              : 'Nothing overdue'}
            {hasOverdue && summary.oldestDueOn && (
              <span className="billing-panel__fact-label">
                {' '}
                oldest due {dateOnlyCell(summary.oldestDueOn)}
              </span>
            )}
          </dd>
        </div>
        <div className="billing-panel__fact">
          <dt className="billing-panel__fact-label">Billing contacts</dt>
          <dd className="billing-panel__fact-value" data-testid="billing-summary-contacts">
            {summary.activeToContactCount} To, {summary.activeCcContactCount} Cc
          </dd>
        </div>
        <div className="billing-panel__fact">
          <dt className="billing-panel__fact-label">Latest invoice</dt>
          <dd className="billing-panel__fact-value" data-testid="billing-summary-latest-invoice">
            {summary.latestInvoice
              ? `${formatMoney(
                  summary.latestInvoice.amountCents,
                  summary.latestInvoice.currency
                )} ${DISPLAY_STATUS_LABEL[summary.latestInvoice.displayStatus].toLowerCase()}`
              : 'None yet'}
          </dd>
        </div>
        <div className="billing-panel__fact">
          <dt className="billing-panel__fact-label">Last reminder</dt>
          <dd className="billing-panel__fact-value" data-testid="billing-summary-last-reminder">
            {summary.lastReminder
              ? `${REMINDER_OUTCOME_LABEL[summary.lastReminder.outcome]} ${instantCell(
                  summary.lastReminder.sentAt ?? summary.lastReminder.claimedAt
                )}`
              : 'Never'}
          </dd>
        </div>
      </dl>
    </AdminPanel>
  );
};
